import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  CircularProgress,
  Alert,
  Typography,
  Box,
  Chip,
  FormGroup,
  FormControlLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ErrorIcon from '@mui/icons-material/Error';
import LaunchIcon from '@mui/icons-material/Launch';
import { buildEventsApiUrl } from '../config/api';
import { buildJsonAuthHeaders } from '../utils/authFetch';

interface Item {
  id: string;
  name: string;
  category: string;
  description?: string;
  images?: string[];
  isAlreadyAdded?: boolean;
}

interface AddItemsModalProps {
  open: boolean;
  onClose: () => void;
  onAddItems: (itemIds: string[]) => Promise<void>;
  eventId: string;
  userId: string;
  eventCategory: string;
  eventName: string;
}

const AddItemsModal: React.FC<AddItemsModalProps> = ({
  open,
  onClose,
  onAddItems,
  eventId,
  userId,
  eventCategory,
  eventName
}) => {
  const [items, setItems] = useState<Item[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectAll, setSelectAll] = useState(false);

  const fetchAvailableItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSelectedItems(new Set());
      setSelectAll(false);

      console.log(`📦 Fetching available items for user ${userId}, event ${eventId}`);

      const response = await fetch(
        buildEventsApiUrl(`/api/events/${eventId}/available-items?userId=${encodeURIComponent(userId)}`),
        {
          method: 'GET',
          headers: buildJsonAuthHeaders(userId)
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch items (${response.status})`);
      }

      const data = await response.json();

      if (data.success && data.items) {
        console.log(`✅ Found ${data.items.length} matching items`);
        setItems(data.items);
      } else {
        setError(data.error || 'Failed to load items');
        setItems([]);
      }
    } catch (err: any) {
      console.error('❌ Error fetching items:', err);
      setError(err.message || 'Failed to load items');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [eventId, userId]);


  // Fetch available items when modal opens
  useEffect(() => {
    if (open) {
      fetchAvailableItems();
    }
  }, [open, fetchAvailableItems]);

  const handleToggleItem = (itemId: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
    
    // Update selectAll state
    setSelectAll(newSelected.size === items.filter(i => !i.isAlreadyAdded).length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems(new Set());
      setSelectAll(false);
    } else {
      const availableIds = items
        .filter(i => !i.isAlreadyAdded)
        .map(i => (i as any).Items?.id || (i as any).id);
      setSelectedItems(new Set(availableIds));
      setSelectAll(true);
    }
  };

  const handleAddItems = async () => {
    if (selectedItems.size === 0) {
      setError('Please select at least one item');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const itemIds = Array.from(selectedItems);
      console.log(`➕ Adding ${itemIds.length} items to event ${eventId}`);

      const response = await fetch(
        buildEventsApiUrl(`/api/events/${eventId}/items`),
        {
          method: 'POST',
          headers: buildJsonAuthHeaders(userId),
          body: JSON.stringify({ itemIds, userId })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to add items');
      }

      console.log(`✅ Successfully added ${itemIds.length} items`);
      await onAddItems(itemIds);
      
      // Reset and close
      setSelectedItems(new Set());
      setSelectAll(false);
      onClose();
    } catch (err: any) {
      console.error('❌ Error adding items:', err);
      setError(err.message || 'Failed to add items');
    } finally {
      setSubmitting(false);
    }
  };

  const availableItems = items.filter(i => !i.isAlreadyAdded);
  const alreadyAddedCount = items.filter(i => i.isAlreadyAdded).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Add Items to "{eventName}"
        <Typography variant="caption" display="block" color="textSecondary">
          Category: {eventCategory}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} icon={<ErrorIcon />}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : availableItems.length === 0 ? (
          <Alert severity="info">
            {items.length === 0 ? (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {`You don't have any items in the "${eventCategory}" category yet.`}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Add items to your personal inventory first, then come back here to add them to this event.
                </Typography>
                <Typography variant="body2">
                  Need help?{' '}
                  <Button
                    component="a"
                    href="#/faq"
                    size="small"
                    endIcon={<LaunchIcon fontSize="inherit" />}
                    sx={{ p: 0, minWidth: 'auto', verticalAlign: 'baseline' }}
                  >
                    Read the FAQ
                  </Button>
                  .
                </Typography>
              </>
            ) : (
              <Typography variant="body2">
                {`All your items in this category have already been added to this event.`}
              </Typography>
            )}
          </Alert>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Select items to add ({selectedItems.size}/{availableItems.length})
                {alreadyAddedCount > 0 && (
                  <Chip
                    label={`${alreadyAddedCount} already added`}
                    size="small"
                    sx={{ ml: 1 }}
                    variant="outlined"
                  />
                )}
              </Typography>

              {availableItems.length > 1 && (
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    }
                    label={`Select all ${availableItems.length} items`}
                  />
                </FormGroup>
              )}
            </Box>

            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {availableItems.map((item) => (
                <ListItem key={(item as any).Items?.id || (item as any).id} disablePadding>
                  <ListItemButton
                    role={undefined}
                    onClick={() => handleToggleItem((item as any).Items?.id || (item as any).id)}
                    dense
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedItems.has((item as any).Items?.id || (item as any).id)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={(item as any).Items?.name || (item as any).name}
                      
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleAddItems}
          variant="contained"
          startIcon={<AddIcon />}
          disabled={submitting || selectedItems.size === 0 || loading}
        >
          {submitting ? 'Adding...' : `Add ${selectedItems.size} Item${selectedItems.size !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddItemsModal;














